import { useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { getPortalPages } from '@nx-frontend/shared/api/webnotes/client';
import { useSearchParams } from '@nx-frontend/shared/hooks/router-proxy';
import { $menuState } from '@nx-frontend/shared/store/feature-menu-tree';
import { changeNoteVisibilityEvent } from '@nx-frontend/shared/store/notes';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { MenuItem, MenuType, PageSettingInPortalPayload } from '@nx-frontend/shared/types';
import { copyToClipboard } from '@nx-frontend/shared/utils';

export function PageSettingsInPortalPopup(params: { content: PageSettingInPortalPayload }) {
  const { content } = params;

  const { globalId, workspaceId } = content;

  const [menuState, changeNoteVisibility, portalSettings] = useUnit([
    $menuState,
    changeNoteVisibilityEvent,
    $portalSettings,
  ]);

  const { toast } = useToast();
  const searchParams = useSearchParams();
  const portalPageUrl = searchParams?.get('portal-page-url');

  const [noteInMenu, setNoteInMenu] = useState<MenuItem | null>(null);

  useEffect(() => {
    let found = false;
    for (const rootType of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER]) {
      for (const menuItemId in menuState[rootType].entities) {
        const item = menuState[rootType].entities[menuItemId];
        if ('targetId' in item && item.targetId === globalId) {
          setNoteInMenu(item);
          found = true;
          break;
        }
      }
      if (found) break;
    }

    if (!found) {
      const fetchData = async () => {
        const portalPages = await getPortalPages({
          workspaceId,
          noteId: globalId,
        });
        if (portalPages) {
          for (const pageItem of portalPages) {
            if (
              pageItem &&
              'noteIds' in pageItem &&
              Array.isArray(pageItem.noteIds) &&
              -1 !== pageItem.noteIds.indexOf(globalId)
            ) {
              for (const rootType of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER]) {
                for (const menuItemId in menuState[rootType].entities) {
                  const item = menuState[rootType].entities[menuItemId];
                  if ('pageId' in item && item.pageId === pageItem.id) {
                    setNoteInMenu(item);
                    return;
                  }
                }
              }
            }
          }
        }
        setNoteInMenu(null);
      };

      fetchData();
    }
  }, [globalId, menuState, workspaceId]);

  const nodeRef = useRef(null);

  let url = '';
  if ((noteInMenu || portalPageUrl) && portalSettings && portalSettings.name) {
    const itemUrl = noteInMenu?.url ?? portalPageUrl;
    url = 'https://' + portalSettings.domain + itemUrl;
  }

  const handleVisibilityChange = useCallback(() => {
    changeNoteVisibility({
      workspaceId,
      globalId,
      visible: !noteInMenu?.visible,
    });
  }, [workspaceId, globalId, noteInMenu?.visible, changeNoteVisibility]);

  const copyUrlToClipboard = useCallback(() => {
    copyToClipboard(url);
    toast({ title: 'Copied!' });
  }, [url, toast]);

  const handleOpenInPortal = useCallback(() => {
    window.open(url);
  }, [url]);

  return (
    <div className="w-[450px]">
      <div className="font-normal uppercase text-[10px] text-neutral-500 leading-[120%] mb-1">Added to portal</div>
      <div className="text-xs not-italic font-normal leading-[130%]">{portalSettings.name}</div>
      <div className="flex mt-2.5 justify-between border-b-neutral-200 border-b border-solid pb-4 mb-4">
        <div className="border border-neutral-200 rounded-lg border-solid bg-neutral-100 flex p-1 pl-2.5 pr-2.5 justify-center items-center">
          <div
            data-cy="page-settings-portal-url"
            className="text-[14px] text-neutral-500 overflow-hidden text-ellipsis whitespace-nowrap w-[225px]"
          >
            {url}
          </div>
          <span onClick={copyUrlToClipboard}>
            <Icon
              className="text-[#000000] opacity-[0.54] ml-1 cursor-pointer hover:opacity-70 w-[16px] h-[16px]"
              icon={IconsMap.COPY}
            ></Icon>
          </span>
        </div>
        <Button type={ButtonType.STROKED} size={ButtonSize.MD} onClick={handleOpenInPortal}>
          Open in portal
        </Button>
      </div>
      {!noteInMenu && (
        <div className="p-4 mt-2 rounded border border-neutral-200 bg-neutral-50 border-solid leading-[120%] tracking-[0.2px] text-[12px]">
          <span className="not-italic font-normal text-neutral-800">
            The page is not published. Please publish the page in the Portal Editor for it to be displayed in the
            portal.
          </span>
        </div>
      )}
      {noteInMenu && !portalPageUrl && (
        <>
          <div className="font-normal uppercase text-[10px] text-neutral-500 leading-[120%] mb-2">
            Status of the page
          </div>
          <div className="text-neutral-800 text-[12px]">
            <Checkbox
              id="visible-in-portal"
              type={CheckboxType.SWITCH}
              checked={Boolean(noteInMenu?.visible)}
              onChange={handleVisibilityChange}
              label={noteInMenu?.visible ? 'Visible' : 'Invisible'}
              boxSize={CheckboxSize.LG}
            />
          </div>
          <CSSTransition
            nodeRef={nodeRef}
            in={noteInMenu?.visible}
            key="page-settings-in-portal-text"
            timeout={200}
            classNames="transition-fade-in"
            unmountOnExit={true}
          >
            <div
              ref={nodeRef}
              className="p-4 mt-2 rounded border border-neutral-200 bg-neutral-50 border-solid leading-[120%] tracking-[0.2px] text-[12px]"
            >
              <span className="not-italic font-normal text-neutral-800">
                Currently everybody sees it. Make it invisible if you don’t want anyone to see it.
              </span>
            </div>
          </CSSTransition>
        </>
      )}
    </div>
  );
}
